import { SectionPrinterSetupModalProps } from '@/renderer/types/printer';
import { X } from 'lucide-react';
import React, { useEffect, useState } from 'react';
import { Button, DropdownSelect, Modal } from '../../common';

export const SectionPrinterSetupModal: React.FC<
  SectionPrinterSetupModalProps
> = ({
  isOpen,
  isSaving,
  canManage = true,
  onClose,
  onSubmit,
  billPrinterId,
  departmentPrinterMap,
  departments,
  outletId,
  printerOptions,
  sectionId,
  sectionName,
}) => {
  const [selectedBillPrinterId, setSelectedBillPrinterId] = useState('');
  const [departmentSelections, setDepartmentSelections] = useState<
    Record<string, string>
  >({});

  useEffect(() => {
    if (!isOpen) return;

    setSelectedBillPrinterId(billPrinterId || '');
    setDepartmentSelections(departmentPrinterMap || {});
  }, [billPrinterId, departmentPrinterMap, isOpen]);

  const hasPrinters = printerOptions.length > 0;
  const hasDepartments = departments.length > 0;
  const allDepartmentsConfigured = departments.every((department) =>
    Boolean(departmentSelections[department.id]),
  );
  const isConfigurationComplete =
    Boolean(selectedBillPrinterId) &&
    hasDepartments &&
    allDepartmentsConfigured;

  const handleDepartmentPrinterChange = (
    departmentId: string,
    printerId: string,
  ) => {
    setDepartmentSelections((previous) => ({
      ...previous,
      [departmentId]: printerId,
    }));
  };

  const handleSubmit = async () => {
    if (!sectionId || !canManage) return;
    if (!isConfigurationComplete) return;

    await onSubmit({
      outlet_id: outletId,
      section_id: sectionId,
      bill_printer_id: selectedBillPrinterId || null,
      department_mappings: departments
        .filter((department) => Boolean(departmentSelections[department.id]))
        .map((department) => ({
          department_id: department.id,
          printer_id: departmentSelections[department.id],
        })),
    });
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} size="lg">
      <div className="space-y-4 text-sm">
        <div className="flex items-center justify-between">
          <h3 className="text-xl font-semibold text-black">
            {sectionName} Printer Setup
          </h3>
          <button
            type="button"
            onClick={onClose}
            className="rounded p-1 text-gray-500 transition-colors hover:bg-gray-100 hover:text-black"
          >
            <X size={22} />
          </button>
        </div>

        {!hasPrinters ? (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-5 text-center text-sm text-gray-600">
            Add at least one printer before configuring this section.
          </div>
        ) : null}

        <div className="space-y-2">
          {departments.map((department) => (
            <div
              key={department.id}
              className="grid grid-cols-[1fr_260px] items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3"
            >
              <span className="font-medium uppercase tracking-wide text-black">
                {department.name}
              </span>
              <DropdownSelect
                value={departmentSelections[department.id] || ''}
                onChange={(v) =>
                  handleDepartmentPrinterChange(department.id, v)
                }
                options={[
                  { value: '', label: 'No printer' },
                  ...printerOptions,
                ]}
                disabled={!hasPrinters || !canManage}
              />
            </div>
          ))}
          <div className="grid grid-cols-[1fr_260px] items-center gap-4 rounded border border-gray-200 bg-white px-4 py-3">
            <span className="font-medium uppercase tracking-wide text-black">
              Bill
            </span>
            <DropdownSelect
              value={selectedBillPrinterId}
              onChange={setSelectedBillPrinterId}
              options={[{ value: '', label: 'No printer' }, ...printerOptions]}
              disabled={!hasPrinters || !canManage}
            />
          </div>
          {!canManage ? (
            <div className="rounded border border-blue-200 bg-blue-50 px-3 py-2 text-sm text-blue-700">
              You can view this printer setup, but only authorized roles can
              change it.
            </div>
          ) : null}
          {departments.length === 0 ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              No active departments found for this outlet. Add departments
              first.
            </div>
          ) : null}
          {hasDepartments && !isConfigurationComplete ? (
            <div className="rounded border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-700">
              Select a bill printer and a printer for every department before
              saving.
            </div>
          ) : null}
        </div>

        <div className="flex justify-end pt-1">
          <Button
            onClick={handleSubmit}
            isLoading={isSaving}
            disabled={
              !sectionId ||
              !hasPrinters ||
              !hasDepartments ||
              !isConfigurationComplete ||
              !canManage
            }
          >
            Save
          </Button>
        </div>
      </div>
    </Modal>
  );
};
